import clsx from 'clsx'
import React from 'react'

import { useOverflowStatus } from '../../hooks/useOverflowStatus'
import { Subaccount } from '../../models/Subaccount'
import { User } from '../../models/User'
import { userColors } from '../../utils/colors'
import { Icon } from '../Icon'
import Avatar from '../app/design/Avatar'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import Tooltip from '../app/design/Tooltip'

type ContactAvatarProps = React.ComponentPropsWithoutRef<typeof Avatar> & {
    user: User | Subaccount
    subaccountParentClientName?: string
    showName?: boolean
    subtitle?: React.ReactNode
    truncateName?: boolean
    containerClassName?: string
    shouldRestrictToOneLetter?: boolean
    hasHoverEffect?: boolean
    iconSVGClassName?: string
}

export const ContactAvatar = ({
    user,
    subaccountParentClientName,
    showName,
    subtitle,
    size = 'md',
    className,
    containerClassName,
    truncateName = false,
    shouldRestrictToOneLetter,
    hasHoverEffect,
    iconSVGClassName,
    ...props
}: ContactAvatarProps) => {
    const ALWAYS_BLUE = '1006'
    const color = userColors(user.id || ALWAYS_BLUE)

    const { overflowing, ref } = useOverflowStatus()

    let avatarContent: React.ReactNode

    if (user.image?.uri) {
        avatarContent = (
            <img
                src={user.image.uri}
                alt={user.name}
                className="size-full rounded-full object-cover"
            />
        )
    }
    else if (user instanceof User && user.name === user.phone) {
        avatarContent = '#'
    }
    else if (user instanceof Subaccount && user.type !== 'child') {
        avatarContent = (
            <Icon
                icon={`${Subaccount.iconNameForSubaccountType(user.type)}.${size === 'md' ? 'large' : 'small'}`}
                svgClassName={iconSVGClassName}
            />
        )
    }
    else {
        const firstInitial = user.name ? user.name[0].toUpperCase() : ''
        const nameParts = user.name?.split(' ') ?? ['', '']
        const lastNameInitial = nameParts.length === 2 ? nameParts.at(-1)![0].toUpperCase() : ''

        if (shouldRestrictToOneLetter) {
            avatarContent = firstInitial
        }
        else {
            avatarContent = `${firstInitial}${lastNameInitial}`
        }
    }

    return (
        <div className={`flex items-center gap-4 ${containerClassName}`}>
            <Avatar
                backgroundColor={
                    user instanceof Subaccount && user.type !== 'child'
                        ? 'bg-primary-100'
                        : color.bg
                }
                foregroundColor={
                    user instanceof Subaccount && user.type !== 'child' ? undefined : color.fg
                }
                size={size}
                className={`group/contact-avatar relative ${className}`}
                {...props}
            >
                {hasHoverEffect && (
                    <div className="absolute inset-0 hidden rounded-full bg-black/20 group-hover/contact-avatar:block"></div>
                )}
                {avatarContent}
            </Avatar>
            {showName && (
                <div className={clsx('flex flex-col gap-1')}>
                    <Tooltip
                        disabled={!overflowing && truncateName}
                        position="top"
                        label={user.name}
                        wrap
                    >
                        <div className={clsx(truncateName && 'max-w-[180px] truncate')} ref={ref}>
                            <Label size="md">
                                {user.name}
                                {subaccountParentClientName && (
                                    <span className="ml-1 font-light text-gray-500">
                                        {subaccountParentClientName}
                                    </span>
                                )}
                            </Label>
                        </div>
                    </Tooltip>
                    {subtitle && typeof subtitle === 'string' && (
                        <Paragraph size="sm" className="text-gray-600">
                            {subtitle}
                        </Paragraph>
                    )}
                    {subtitle && typeof subtitle !== 'string' && subtitle}
                </div>
            )}
        </div>
    )
}
